import { Checkbox, NumberInput, Select, SimpleGrid, Stack, Text } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useDebouncedValue } from '@mantine/hooks';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, podatki, type Shema } from '../api/odjemalec';
import { Kartica } from '../postavitev/Podatki';
import { GlavaZaslona, ZaslonSeznama } from '../seznam/Seznam';
import { RazclenitevDmv } from '../vozila/RazclenitevDmv';

type Izbira = Shema['IzbiraPodatki'];
const izbire = (s?: Izbira[]) => (s ?? []).map((i) => ({ value: i.koda ?? '', label: i.naziv ?? '' }));

const danes = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Izračun DMV – kot IzracunajDmvOkno v Swingu (Moduli → Izračun DMV): samostojni informativni izračun po ZDMV-1 za
 * vneseno vozilo; izračuna ga strežnik (enako kot kalkulator FURS) med vnosom.
 */
export function IzracunDmv() {
  const [z, setZ] = useState<Shema['DmvZahteva']>({ mocMotorja: 0, datumNabave: danes(), dmvCikel: 'WLTP' });
  const nastavi = (k: keyof Shema['DmvZahteva'], v: unknown) => setZ((p) => ({ ...p, [k]: v }));
  const vrste = useQuery({
    queryKey: ['vrste-vozil'],
    queryFn: ({ signal }) => podatki(api.GET('/api/vrste-vozil', { signal })),
  });
  const sifranti = useQuery({
    queryKey: ['dmv-sifranti'],
    queryFn: ({ signal }) => podatki(api.GET('/api/dmv/sifranti', { signal })),
  });
  const [zakasnjeno] = useDebouncedValue(z, 300);
  const izracun = useQuery({
    queryKey: ['dmv-izracun', zakasnjeno],
    queryFn: ({ signal }) => podatki(api.POST('/api/dmv/izracun', { signal, body: zakasnjeno })),
    placeholderData: keepPreviousData,
  });
  const datum = (k: 'datumPrveReg' | 'datumNabave', oznaka: string) => (
    <DatePickerInput
      label={oznaka}
      value={z[k] ?? null}
      onChange={(v) => nastavi(k, v ?? undefined)}
      valueFormat="D. M. YYYY"
      clearable
    />
  );
  const stevilo = (k: 'mocMotorja' | 'dmvCo2' | 'dmvNajvecjaHitrost', oznaka: string) => (
    <NumberInput
      size="md"
      label={oznaka}
      value={z[k] ?? ''}
      onChange={(v) => nastavi(k, typeof v === 'number' ? v : k === 'mocMotorja' ? 0 : undefined)}
      decimalSeparator=","
      min={0}
    />
  );
  const izbira = (k: 'dmvGorivo' | 'dmvCikel' | 'dmvEuro', oznaka: string, s?: Izbira[]) => (
    <Select
      label={oznaka}
      data={izbire(s)}
      value={z[k] ?? null}
      onChange={(v) => nastavi(k, v ?? undefined)}
      placeholder="Ni vneseno"
      clearable
    />
  );

  return (
    <ZaslonSeznama>
      <GlavaZaslona
        naslov="Izračun DMV"
        opis="Informativni izračun davka na motorna vozila po ZDMV-1."
        pomoc="izracun-dmv"
      />
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <Kartica naslov="Podatki o vozilu">
          <Stack gap="sm">
            <Select
              label="Vrsta vozila"
              data={[...new Set((vrste.data ?? []).map((v) => v.naziv ?? ''))]}
              value={z.vrsta ?? null}
              onChange={(v) => nastavi('vrsta', v ?? undefined)}
              placeholder="Izberite vrsto"
              searchable
            />
            <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="sm">
              {stevilo('mocMotorja', 'Moč motorja (kW)')}
              {izbira('dmvGorivo', 'Gorivo', sifranti.data?.goriva)}
              {datum('datumPrveReg', 'Prva registracija')}
              {datum('datumNabave', 'Datum pridobitve')}
              {stevilo('dmvCo2', 'Izpust CO2 (g/km)')}
              {izbira('dmvCikel', 'Meritev CO2', sifranti.data?.cikli)}
              {izbira('dmvEuro', 'Standard EURO', sifranti.data?.euro)}
              {stevilo('dmvNajvecjaHitrost', 'Največja hitrost (km/h)')}
            </SimpleGrid>
            <Checkbox
              label="Več kot 8 sedežev"
              checked={!!z.dmvOsemSedezev}
              onChange={(e) => nastavi('dmvOsemSedezev', e.currentTarget.checked)}
            />
            <Checkbox
              label="Osebno večnamensko vozilo"
              checked={!!z.dmvVecnamensko}
              onChange={(e) => nastavi('dmvVecnamensko', e.currentTarget.checked)}
            />
            <Checkbox
              label="Novo vozilo (še ni bilo registrirano)"
              checked={!!z.dmvNovo}
              onChange={(e) => nastavi('dmvNovo', e.currentTarget.checked)}
            />
          </Stack>
        </Kartica>
        <Kartica naslov="Izračun">
          <RazclenitevDmv izracun={izracun.data} />
          <Text size="xs" c="dimmed" mt="md">
            Informativni izračun po ZDMV-1 (velja od 1. 1. 2021), enako kot kalkulator FURS. Davek dokončno odmeri FURS.
          </Text>
        </Kartica>
      </SimpleGrid>
    </ZaslonSeznama>
  );
}
